import React, { useState, useEffect } from "react";
import { Rnd } from "react-rnd";
import type { Position } from "../../../models/Position";

interface PdfOverlayBoxProps {
  defaultPosition: Position;
  pdfHeight: number;
  scale: number;
  bounds?: "parent" | HTMLElement | string;
  onChange?: (pos: Position) => void;
  children?: React.ReactNode;
  isDraggable?: boolean;
  isResizable?: boolean;
}

const PdfOverlayBox: React.FC<PdfOverlayBoxProps> = ({
  defaultPosition,
  pdfHeight,
  scale,
  bounds = "parent",
  onChange,
  children,
  isDraggable = true,
  isResizable = true,
}) => {

  const convertPdfToScreen = (p: Position) => {
    const x = p.bottomLeftX * scale;
    const y = pdfHeight - p.topRightY * scale;
    const width = (p.topRightX - p.bottomLeftX) * scale;
    const height = (p.topRightY - p.bottomLeftY) * scale;
    return { x, y, width, height };
  };

  const convertScreenToPdf = (
    x: number,
    y: number,
    width: number,
    height: number
  ): Position => {
    const pdfX = x / scale;
    const pdfY = (pdfHeight - y - height) / scale;
    return {
      bottomLeftX: pdfX,
      bottomLeftY: pdfY,
      topRightX: pdfX + width / scale,
      topRightY: pdfY + height / scale,
    };
  };

  const [box, setBox] = useState(() => convertPdfToScreen(defaultPosition));

  useEffect(() => {
    setBox(convertPdfToScreen(defaultPosition));
  }, [defaultPosition, pdfHeight, scale]);

  return (
    <Rnd
      size={{ width: box.width, height: box.height }}
      position={{ x: box.x, y: box.y }}
      bounds={bounds}
      onDragStop={(_e, d) => {
        const newPdfPos = convertScreenToPdf(d.x, d.y, box.width, box.height);
        onChange?.(newPdfPos);
        setBox({ ...box, x: d.x, y: d.y });
      }}
      onResizeStop={(_e, _direction, ref, _delta, pos) => {
        const width = parseFloat(ref.style.width);
        const height = parseFloat(ref.style.height);
        const newPdfPos = convertScreenToPdf(pos.x, pos.y, width, height);
        onChange?.(newPdfPos);
        setBox({ x: pos.x, y: pos.y, width, height });
      }}
      style={{
        zIndex: 999,
      }}
      disableDragging={!isDraggable}
      enableResizing={isResizable ? undefined : false}
    >
      {children}
    </Rnd>
  );
};

export default PdfOverlayBox;
